import { lazy, Suspense } from 'react'
import { Routes, Route, Navigate } from 'react-router-dom'
import Layout from './components/Layout'

// Critical pages - loaded immediately
import Home from './pages/Home'

// Lazy loaded pages - loaded on demand
const Video = lazy(() => import('./pages/Video'))
const TestPlayer = lazy(() => import('./pages/TestPlayer'))
const Models = lazy(() => import('./pages/Models'))
const Categories = lazy(() => import('./pages/Categories'))
const Tags = lazy(() => import('./pages/Tags'))
const Premium = lazy(() => import('./pages/Premium'))
const Contact = lazy(() => import('./pages/Contact'))
const Search = lazy(() => import('./pages/Search'))
const Upload = lazy(() => import('./pages/Upload'))
const Dashboard = lazy(() => import('./pages/Dashboard'))
const Membership = lazy(() => import('./pages/Membership'))
const CreatorApplication = lazy(() => import('./pages/CreatorApplication'))
const AffiliateHub = lazy(() => import('./pages/AffiliateHub'))
const CreatorRecruitment = lazy(() => import('./pages/CreatorRecruitment'))
const CampaignLanding = lazy(() => import('./pages/CampaignLanding'))

// Settings Pages
const Connections = lazy(() => import('./pages/Connections'))
const Analytics = lazy(() => import('./pages/Analytics'))

// User Pages - lazy loaded
const Profile = lazy(() => import('./pages/Profile'))
const History = lazy(() => import('./pages/History'))
const Favorites = lazy(() => import('./pages/Favorites'))

// Test Pages - lazy loaded
const TestCloudinary = lazy(() => import('./pages/TestCloudinary'))

// Admin Pages - lazy loaded
const OneDriveManager = lazy(() => import('./pages/OneDriveManager'))
const EditVideo = lazy(() => import('./pages/EditVideo'))

// Creator Studio - lazy loaded
const Studio = lazy(() => import('./pages/Studio'))
const StudioUpload = lazy(() => import('./pages/StudioUpload'))
const StudioEdit = lazy(() => import('./pages/StudioEdit'))

// Footer Pages - lazy loaded
const About = lazy(() => import('./pages/About'))
const Careers = lazy(() => import('./pages/Careers'))
const Press = lazy(() => import('./pages/Press'))
const Blog = lazy(() => import('./pages/Blog'))
const FAQ = lazy(() => import('./pages/FAQ'))
const Help = lazy(() => import('./pages/Help'))
const Safety = lazy(() => import('./pages/Safety'))
const Terms = lazy(() => import('./pages/Terms'))
const Privacy = lazy(() => import('./pages/Privacy'))
const Cookies = lazy(() => import('./pages/Cookies'))
const DMCA = lazy(() => import('./pages/DMCA'))
const BecomeModel = lazy(() => import('./pages/BecomeModel'))
const Partners = lazy(() => import('./pages/Partners'))
const Advertise = lazy(() => import('./pages/Advertise'))

// Loading fallback component
function PageLoader() {
  return (
    <div className="flex min-h-[50vh] items-center justify-center text-neutral-400">
      <div className="text-center">
        <div className="mx-auto mb-4 h-10 w-10 animate-spin rounded-full border-4 border-neutral-700 border-t-emerald-500" />
        <span>Loading...</span>
      </div>
    </div>
  )
}

function App() {
  return (
    <Routes>
      <Route path="/" element={<Layout />}>
        <Route index element={<Home />} />
        <Route path="video/:id" element={<Suspense fallback={<PageLoader />}><Video /></Suspense>} />
        <Route path="test-player" element={<Suspense fallback={<PageLoader />}><TestPlayer /></Suspense>} />
        <Route path="test-cloudinary" element={<Suspense fallback={<PageLoader />}><TestCloudinary /></Suspense>} />
        <Route path="models" element={<Suspense fallback={<PageLoader />}><Models /></Suspense>} />
        <Route path="categories" element={<Suspense fallback={<PageLoader />}><Categories /></Suspense>} />
        <Route path="category/:categoryName" element={<Suspense fallback={<PageLoader />}><Categories /></Suspense>} />
        <Route path="tags" element={<Suspense fallback={<PageLoader />}><Tags /></Suspense>} />
        <Route path="tag/:tagName" element={<Suspense fallback={<PageLoader />}><Tags /></Suspense>} />
        <Route path="premium" element={<Suspense fallback={<PageLoader />}><Premium /></Suspense>} />
        <Route path="membership" element={<Suspense fallback={<PageLoader />}><Membership /></Suspense>} />
        <Route path="contact" element={<Suspense fallback={<PageLoader />}><Contact /></Suspense>} />
        <Route path="search" element={<Suspense fallback={<PageLoader />}><Search /></Suspense>} />
        <Route path="upload" element={<Suspense fallback={<PageLoader />}><Upload /></Suspense>} />
        <Route path="dashboard" element={<Suspense fallback={<PageLoader />}><Dashboard /></Suspense>} />
        <Route path="creator/apply" element={<Suspense fallback={<PageLoader />}><CreatorApplication /></Suspense>} />
        <Route path="creators" element={<Suspense fallback={<PageLoader />}><CreatorRecruitment /></Suspense>} />
        <Route path="casting" element={<Navigate to="/casting/global" replace />} />
        <Route path="casting/:cityId" element={<Suspense fallback={<PageLoader />}><CampaignLanding /></Suspense>} />
        <Route path="affiliate" element={<Suspense fallback={<PageLoader />}><AffiliateHub /></Suspense>} />
        <Route path="affiliates" element={<Suspense fallback={<PageLoader />}><AffiliateHub /></Suspense>} />
        {/* User Pages */}
        <Route path="profile" element={<Suspense fallback={<PageLoader />}><Profile /></Suspense>} />
        <Route path="history" element={<Suspense fallback={<PageLoader />}><History /></Suspense>} />
        <Route path="favorites" element={<Suspense fallback={<PageLoader />}><Favorites /></Suspense>} />
        {/* Admin Pages */}
        <Route path="admin/content" element={<Suspense fallback={<PageLoader />}><OneDriveManager /></Suspense>} />
        <Route path="edit/:videoId" element={<Suspense fallback={<PageLoader />}><EditVideo /></Suspense>} />
        {/* Creator Studio & Settings */}
        <Route path="connections" element={<Suspense fallback={<PageLoader />}><Connections /></Suspense>} />
        <Route path="analytics" element={<Suspense fallback={<PageLoader />}><Analytics /></Suspense>} />
        <Route path="studio" element={<Suspense fallback={<PageLoader />}><Studio /></Suspense>} />
        <Route path="studio/upload" element={<Suspense fallback={<PageLoader />}><StudioUpload /></Suspense>} />
        <Route path="studio/video/:videoId" element={<Suspense fallback={<PageLoader />}><StudioEdit /></Suspense>} />
        {/* Footer Pages */}
        <Route path="about" element={<Suspense fallback={<PageLoader />}><About /></Suspense>} />
        <Route path="careers" element={<Suspense fallback={<PageLoader />}><Careers /></Suspense>} />
        <Route path="press" element={<Suspense fallback={<PageLoader />}><Press /></Suspense>} />
        <Route path="blog" element={<Suspense fallback={<PageLoader />}><Blog /></Suspense>} />
        <Route path="faq" element={<Suspense fallback={<PageLoader />}><FAQ /></Suspense>} />
        <Route path="help" element={<Suspense fallback={<PageLoader />}><Help /></Suspense>} />
        <Route path="safety" element={<Suspense fallback={<PageLoader />}><Safety /></Suspense>} />
        <Route path="terms" element={<Suspense fallback={<PageLoader />}><Terms /></Suspense>} />
        <Route path="privacy" element={<Suspense fallback={<PageLoader />}><Privacy /></Suspense>} />
        <Route path="cookies" element={<Suspense fallback={<PageLoader />}><Cookies /></Suspense>} />
        <Route path="dmca" element={<Suspense fallback={<PageLoader />}><DMCA /></Suspense>} />
        <Route path="become-model" element={<Suspense fallback={<PageLoader />}><BecomeModel /></Suspense>} />
        <Route path="partners" element={<Suspense fallback={<PageLoader />}><Partners /></Suspense>} />
        <Route path="advertise" element={<Suspense fallback={<PageLoader />}><Advertise /></Suspense>} />
      </Route>
    </Routes>
  )
}

export default App
